import type {
  AdminAuditLogResponse,
  AdminDashboardStats,
  AdminLedgerEntryResponse,
  AdminListAuditLogsQuery,
  AdminListListingsQuery,
  AdminListingListResponse,
  AdminListOrdersQuery,
  AdminAccount,
  AdminSecuritySettings,
  ConfirmMyMfaRequest,
  MyMfaSetupResponse,
  UpdateAdminMfaSettingRequest,
  AdminListRoyaltyReportsQuery,
  AdminPolicy,
  AdminPolicyDetail,
  AdminRoyaltyReportCountsResponse,
  AdminRoyaltyReportListResponse,
  CreateAdminRequest,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  PolicyVersionDetail,
  ResetAdminPasswordRequest,
  UpsertPolicyContentRequest,
  AdminChatMessageResponse,
  AdminChatRoomDetailResponse,
  AdminChatRoomResponse,
  AdminGetChatMessagesQuery,
  AdminJoinChatRoomResponse,
  AdminLeaveChatRoomResponse,
  AdminListChatRoomsQuery,
  AdminListUsersQuery,
  AdminListRefundsQuery,
  AdminListDepositReturnsQuery,
  AdminListPaymentsQuery,
  AdminPaymentListResponse,
  AdminSalesQuery,
  AdminSalesSummary,
  AdminSalesEntriesQuery,
  AdminSalesEntriesResponse,
  AdminDepositReturnListResponse,
  AdminListSponsorshipsQuery,
  AdminListSponsorshipApplicationsQuery,
  AdminSponsorshipApplicationPage,
  AdminSponsorshipPage,
  RejectSponsorshipRequest,
  RevokeSocialChannelRequest,
  AdminListSettlementsQuery,
  AdminOrderListResponse,
  AdminOrderResponse,
  AdminDisputeEvidenceResponse,
  AdminOrderTodoCounts,
  AdminRefundListResponse,
  AdminRefundResponse,
  AdminSettlementListResponse,
  AdminUserDetailResponse,
  OperatorSignInResponse,
  AdminUserListResponse,
  AdminSignInRequest,
  AdminSignInResponse,
  ChangeMyPasswordRequest,
  UpdateMyProfileRequest,
  AdminTokensResponse,
  CancelOrderRequest,
  CreateRefundRequest,
  DismissDisputeRequest,
  ResolveViolationReportRequest,
  RejectListingRequest,
  RejectRefundRequest,
  ConfirmPaymentRequest,
  HoldSettlementRequest,
  SuspendListingRequest,
  VoidRoyaltyReportRequest,
  ConfirmRoyaltyDepositRequest,
  AdjustRoyaltyReportRequest,
  SuspendUserRequest,
  PlatformSettings,
  PlatformAssetKey,
  PlatformAssetMeta,
  UpdatePaymentSettingsRequest,
  UpdateSiteSettingsRequest,
  UpdateCommunitySettingsRequest,
  UpdateSponsorshipSettingsRequest,
  UpdateCompanySettingsRequest,
  UpdateListingSettingsRequest,
  UpdatePlatformSocialLinksRequest,
  AdminSocialChannelRequest,
  AdminSocialChannelRequestPage,
  ApproveSocialChannelRequest,
  ListSocialChannelRequestsQuery,
  RejectSocialChannelRequest,
  AppVersionResponse,
  UpdateAppVersionRequest,
  SchedulerJobResponse,
  UpdateSchedulerJobRequest,
  AdminTerminationPreview,
  TerminateOrderRequest,
  CancelTerminationRequest,
  ProposeLicenseAmendmentRequest,
  ResumeLicenseRequest,
  SuspendLicenseRequest,
  AdminReviveCheck,
  CompleteDepositReturnRequest,
  PublishPolicyVersionRequest,
  AdminChatReportPage,
  AdminCommunityReportPage,
  ListReportsQuery,
  ReportPendingCount,
  AdminSupportInquiryDetail,
  AdminSupportInquiryPage,
  ListSupportInquiriesQuery,
  ResolveChatReportRequest,
  ResolveCommunityReportRequest,
  CollectPayoutClawbackRequest,
  ReturnDepositRequest,
  ReviveOrderRequest,
  UpdateDepositReturnAccountRequest,
  SeenVersion,
  TakeDownSponsorshipRequest,
  UnsuspendUserRequest,
  AdminListProposalsQuery,
  AdminProposalListResponse,
  CloseProposalRequest,
  AdminCommunityPostDetail,
  AdminCommunityPostPage,
  ListAdminCommunityPostsQuery,
  RemoveCommunityContentRequest,
} from '@inflip/contracts/admin';
import {
  koreanErrorMessage,
  type AppPlatform,
  type PolicyLanguage,
  type SchedulerJobKey,
} from '@inflip/shared';

import { dedupeWrite, writeKey } from './dedupe-write';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:4102';

async function request<T>(
  base: string,
  path: string,
  init?: RequestInit & { token?: string; query?: Record<string, string | number | undefined> },
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (init?.token) headers.authorization = `Bearer ${init.token}`;
  if (init?.headers) Object.assign(headers, init.headers as Record<string, string>);

  const qs = init?.query
    ? '?' +
      new URLSearchParams(
        Object.entries(init.query).filter(([, v]) => v !== undefined) as [string, string][],
      ).toString()
    : '';

  const url = `${base}/api/v1${path}${qs}`;
  return dedupeWrite(writeKey(init?.method, url, init?.token, init?.body), async () => {
    const res = await apiFetch(url, {
      ...init,
      headers,
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await humanizeError(res));
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  });
}

/** 네트워크가 끊기면 브라우저가 영어("Failed to fetch")를 던진다 — 한국어 안내로 바꾼다 */
async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error('서버에 연결하지 못했어요. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.');
  }
}

/** 서버 AdminMfaGuard 의 오류 코드 (@inflip/auth ADMIN_MFA_REQUIRED) */
const ADMIN_MFA_REQUIRED_CODE = 'ADMIN_MFA_REQUIRED';
/** OTP 없이 로그인한 세션이 2단계 인증에 막혔을 때 window 로 보내는 이벤트 — auth-context 가 듣는다 */
export const ADMIN_MFA_REQUIRED_EVENT = 'inflip-admin:mfa-required';

/** NestJS 에러 응답의 message 를 꺼내 사용자에게 보여줄 문구로 (JSON 이 아니면 원문 유지) */
async function humanizeError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const parsed = JSON.parse(text) as {
      code?: string;
      message?: string | string[];
      errors?: { message?: string }[];
    };
    // 2단계 인증이 켜져 OTP 없이 로그인한 세션이 막힘 — 인증 상태가 안내와 함께 로그인 화면으로 보낸다
    if (res.status === 403 && parsed.code === ADMIN_MFA_REQUIRED_CODE) {
      window.dispatchEvent(new Event(ADMIN_MFA_REQUIRED_EVENT));
    }
    // nestjs-zod 검증 실패는 message 가 'Validation failed' 뿐 — errors 의 실제 사유 우선
    const zodMessage = parsed.errors
      ?.map((e) => e.message)
      .filter(Boolean)
      .join('\n');
    if (zodMessage) return zodMessage;
    if (parsed.message) {
      return Array.isArray(parsed.message) ? parsed.message.join('\n') : parsed.message;
    }
  } catch {
    // JSON 아님 — 아래 원문 fallback
  }
  // JSON 이 아닌 응답(프록시 오류 페이지 등) — 원문 대신 상태별 한국어 안내
  return koreanErrorMessage(res.status, null);
}

/** multipart 전송 — JSON request 와 달리 content-type 을 지정하지 않는다 (boundary 자동) */
async function requestMultipart<T>(path: string, token: string, body: FormData): Promise<T> {
  const url = `${BASE}/api/v1${path}`;
  return dedupeWrite(writeKey('PUT', url, token, body), async () => {
    const res = await apiFetch(url, {
      method: 'PUT',
      headers: { authorization: `Bearer ${token}` },
      credentials: 'include',
      body,
    });
    if (!res.ok) throw new Error(await humanizeError(res));
    return res.json() as Promise<T>;
  });
}

/** API 가 내려준 상대 경로(협찬 표지 등)를 어드민 화면에서 바로 쓸 수 있는 주소로 */
export function adminAssetUrl(path: string): string {
  return `${BASE}/api/v1${path}`;
}

/** 사용자 API — 공개 파일(상품 썸네일)은 사용자 API 가 내려준다 */
const USER_API_BASE =
  (import.meta.env.VITE_USER_API_BASE_URL as string | undefined) ?? 'http://localhost:4101';

/** 사용자 API 기준 상대 경로(상품 썸네일 등)를 어드민 화면에서 열 주소로 */
export function userApiAssetUrl(path: string): string {
  return `${USER_API_BASE}/api/v1${path}`;
}

export const api = {
  // ── 내 정보 ──
  me: (token: string) => request<AdminAccount>(BASE, '/me', { token }),
  updateMe: (token: string, body: UpdateMyProfileRequest) =>
    request<AdminAccount>(BASE, '/me', { method: 'PATCH', token, body: JSON.stringify(body) }),
  changeMyPassword: (token: string, body: ChangeMyPasswordRequest) =>
    request<{ ok: true }>(BASE, '/me/password', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),

  // ── 어드민 계정 관리 (SUPER_ADMIN) ──
  listAdmins: (token: string) => request<AdminAccount[]>(BASE, '/admins', { token }),
  createAdmin: (token: string, body: CreateAdminRequest) =>
    request<AdminAccount>(BASE, '/admins', { method: 'POST', token, body: JSON.stringify(body) }),
  resetAdminPassword: (token: string, id: string, body: ResetAdminPasswordRequest) =>
    request<{ ok: true }>(BASE, `/admins/${id}/password`, {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  deactivateAdmin: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/admins/${id}`, { method: 'DELETE', token }),
  /** OTP 초기화 — 다음 로그인에서 다시 등록 */
  resetAdminMfa: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/admins/${id}/mfa`, { method: 'DELETE', token }),

  // ── 보안 설정 (관리자 2단계 OTP 인증) ──
  getSecuritySettings: (token: string) =>
    request<AdminSecuritySettings>(BASE, '/security', { token }),
  updateAdminMfaSetting: (token: string, body: UpdateAdminMfaSettingRequest) =>
    request<AdminSecuritySettings>(BASE, '/security/admin-mfa', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  setupMyMfa: (token: string) =>
    request<MyMfaSetupResponse>(BASE, '/security/my-otp/setup', {
      method: 'POST',
      token,
      body: '{}',
    }),
  confirmMyMfa: (token: string, body: ConfirmMyMfaRequest) =>
    request<AdminSecuritySettings>(BASE, '/security/my-otp/confirm', {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  // ── 약관 버전 관리 ──
  listPolicies: (token: string) => request<AdminPolicy[]>(BASE, '/policies', { token }),
  createPolicy: (token: string, body: CreatePolicyRequest) =>
    request<AdminPolicy>(BASE, '/policies', { method: 'POST', token, body: JSON.stringify(body) }),
  updatePolicy: (token: string, id: string, body: UpdatePolicyRequest) =>
    request<AdminPolicy>(BASE, `/policies/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  getPolicy: (token: string, id: string) =>
    request<AdminPolicyDetail>(BASE, `/policies/${id}`, { token }),
  createPolicyVersion: (token: string, id: string) =>
    request<PolicyVersionDetail>(BASE, `/policies/${id}/versions`, { method: 'POST', token }),
  getPolicyVersion: (token: string, id: string, versionId: string) =>
    request<PolicyVersionDetail>(BASE, `/policies/${id}/versions/${versionId}`, { token }),
  /** 한 언어의 제목·본문 저장 (본문은 에디터 HTML — 서버가 허용 태그만 남긴다) */
  savePolicyContent: (
    token: string,
    id: string,
    versionId: string,
    language: PolicyLanguage,
    body: UpsertPolicyContentRequest,
  ) =>
    request<PolicyVersionDetail>(
      BASE,
      `/policies/${id}/versions/${versionId}/contents/${language}`,
      { method: 'PUT', token, body: JSON.stringify(body) },
    ),
  publishPolicyVersion: (
    token: string,
    id: string,
    versionId: string,
    body: PublishPolicyVersionRequest = {},
  ) =>
    request<PolicyVersionDetail>(BASE, `/policies/${id}/versions/${versionId}/publish`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  deletePolicyVersion: (token: string, id: string, versionId: string) =>
    request<{ ok: true }>(BASE, `/policies/${id}/versions/${versionId}`, {
      method: 'DELETE',
      token,
    }),

  // ── 로열티 정산 ──
  listRoyaltyReports: (token: string, query: Partial<AdminListRoyaltyReportsQuery>) =>
    request<AdminRoyaltyReportListResponse>(BASE, '/royalty-reports', {
      token,
      query: query as Record<string, string>,
    }),
  royaltyReportCounts: (token: string) =>
    request<AdminRoyaltyReportCountsResponse>(BASE, '/royalty-reports/counts', { token }),
  confirmRoyaltyReport: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/royalty-reports/${id}/confirm`, { method: 'POST', token }),
  payOutRoyaltyReport: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/royalty-reports/${id}/payout`, { method: 'POST', token }),
  /** 첨부(구매자 증빙·판매자 이의 자료) 내려받기 — blob */
  royaltyReportFile: async (token: string, id: string, fileId: string): Promise<Blob> => {
    const res = await apiFetch(`${BASE}/api/v1/royalty-reports/${id}/files/${fileId}`, {
      headers: { authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await humanizeError(res));
    return res.blob();
  },
  /** 로열티 입금 확인 — 같은 계약의 보고 여러 건을 실제 금액·입금자명으로 한 번에 */
  confirmRoyaltyDeposit: (token: string, body: ConfirmRoyaltyDepositRequest) =>
    request<{ ok: true }>(BASE, '/royalty-reports/confirm-deposit', {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 판매자 이의 기각 */
  dismissRoyaltyObjection: (token: string, objectionId: string, body: { reason: string }) =>
    request<{ ok: true }>(BASE, `/royalty-reports/objections/${objectionId}/dismiss`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 입금 확인 전 보고 반려 */
  voidRoyaltyReport: (token: string, id: string, body: VoidRoyaltyReportRequest) =>
    request<{ ok: true }>(BASE, `/royalty-reports/${id}/void`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 입금 확인·지급 이후 매출 정정 — 차액 보고 생성 */
  adjustRoyaltyReport: (token: string, id: string, body: AdjustRoyaltyReportRequest) =>
    request<{ ok: true }>(BASE, `/royalty-reports/${id}/adjust`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  // ── dashboard / users / audit ──
  dashboardStats: (token: string) =>
    request<AdminDashboardStats>(BASE, '/dashboard/stats', { token }),
  listUsers: (token: string, query: Partial<AdminListUsersQuery>) =>
    request<AdminUserListResponse>(BASE, '/users', {
      token,
      query: query as Record<string, string>,
    }),
  getUser: (token: string, id: string) =>
    request<AdminUserDetailResponse>(BASE, `/users/${id}`, { token }),
  suspendUser: (token: string, id: string, body: SuspendUserRequest) =>
    request<{ ok: true }>(BASE, `/users/${id}/suspend`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  revokeSocialChannel: (
    token: string,
    userId: string,
    platform: string,
    body: RevokeSocialChannelRequest,
  ) =>
    request<{ ok: true }>(BASE, `/users/${userId}/social-channels/${platform}/revoke`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  unsuspendUser: (token: string, id: string, body: UnsuspendUserRequest) =>
    request<{ ok: true }>(BASE, `/users/${id}/unsuspend`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 간편비밀번호 초기화 — 고객센터 본인 확인 후 */
  resetUserSimplePin: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/users/${id}/simple-pin/reset`, { method: 'POST', token }),
  /** 운영 계정으로 웹 열기 — 1분 안에 한 번 쓰는 로그인 주소 */
  operatorSignIn: (token: string, id: string) =>
    request<OperatorSignInResponse>(BASE, `/users/${id}/operator-sign-in`, {
      method: 'POST',
      token,
    }),
  listAuditLogs: (token: string, query: Partial<AdminListAuditLogsQuery>) =>
    request<AdminAuditLogResponse[]>(BASE, '/audit-logs', {
      token,
      query: query as Record<string, string>,
    }),
  orderLedger: (token: string, orderId: string) =>
    request<AdminLedgerEntryResponse[]>(BASE, `/orders/${orderId}/ledger`, { token }),

  // ── listings 심사 ──
  listListings: (token: string, query: Partial<AdminListListingsQuery>) =>
    request<AdminListingListResponse>(BASE, '/listings', {
      token,
      query: query as Record<string, string>,
    }),
  publishListing: (token: string, id: string, body: SeenVersion) =>
    request<{ ok: true }>(BASE, `/listings/${id}/publish`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  rejectListing: (token: string, id: string, body: RejectListingRequest) =>
    request<{ ok: true }>(BASE, `/listings/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  suspendListing: (token: string, id: string, body: SuspendListingRequest) =>
    request<{ ok: true }>(BASE, `/listings/${id}/suspend`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  unsuspendListing: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/listings/${id}/unsuspend`, { method: 'POST', token }),
  /** 판매 중 리스팅에 추가된 오퍼(PENDING) 단위 심사 */
  approveListingOffer: (token: string, listingId: string, offerId: string, body: SeenVersion) =>
    request<{ ok: true }>(BASE, `/listings/${listingId}/offers/${offerId}/approve`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  rejectListingOffer: (
    token: string,
    listingId: string,
    offerId: string,
    body: RejectListingRequest,
  ) =>
    request<{ ok: true }>(BASE, `/listings/${listingId}/offers/${offerId}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  refresh: () =>
    request<AdminTokensResponse>(BASE, '/auth/refresh', { method: 'POST', body: '{}' }),
  logout: () => request<{ ok: true }>(BASE, '/auth/logout', { method: 'POST', body: '{}' }),
  signIn: (body: AdminSignInRequest) =>
    request<AdminSignInResponse>(BASE, '/auth/signin', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listOrders: (token: string, query: Partial<AdminListOrdersQuery>) =>
    request<AdminOrderListResponse>(BASE, '/orders', {
      token,
      query: query as Record<string, string>,
    }),
  /** 상태 칩 옆 숫자 — 처리할 일이 쌓이는 상태만 */
  orderTodoCounts: (token: string) =>
    request<AdminOrderTodoCounts>(BASE, '/orders/todo-counts', { token }),
  getOrder: (token: string, id: string) =>
    request<AdminOrderResponse>(BASE, `/orders/${id}`, { token }),
  /** 분쟁 증거 이미지 보기 주소 (10분) — recordId 면 지난 분쟁 것 */
  disputeEvidence: (token: string, id: string, recordId?: string) =>
    request<AdminDisputeEvidenceResponse>(BASE, `/orders/${id}/dispute-evidence`, {
      token,
      query: recordId ? { recordId } : undefined,
    }),
  /** 판매자 위반 신고 증거 이미지 보기 주소 (10분) */
  violationReportEvidence: (token: string, id: string, reportId: string) =>
    request<AdminDisputeEvidenceResponse>(
      BASE,
      `/orders/${id}/violation-reports/${reportId}/evidence`,
      { token },
    ),
  /** 판매자 위반 신고 처리 — 조치 완료·기각 */
  resolveViolationReport: (
    token: string,
    id: string,
    reportId: string,
    body: ResolveViolationReportRequest,
  ) =>
    request<{ ok: true }>(BASE, `/orders/${id}/violation-reports/${reportId}/resolve`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  cancelOrder: (token: string, id: string, body: CancelOrderRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/cancel`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  // ── 입금 예외 처리 (초과·부족·만료 후 입금) ──
  /** 부족 입금 반환(주문 취소) · 만료 후 입금 반환 */
  returnDeposit: (token: string, id: string, body: ReturnDepositRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/deposit-returns`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  reviveCheck: (token: string, id: string) =>
    request<AdminReviveCheck>(BASE, `/orders/${id}/revive-check`, { token }),
  /** 만료 주문 되살리기 — 늦게 들어온 입금 확인 */
  reviveOrder: (token: string, id: string, body: ReviveOrderRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/revive`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  collectPayoutClawback: (token: string, clawbackId: string, body: CollectPayoutClawbackRequest) =>
    request<{ ok: true }>(BASE, `/orders/clawbacks/${clawbackId}/collect`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  completeDepositReturn: (token: string, returnId: string, body: CompleteDepositReturnRequest) =>
    request<{ ok: true }>(BASE, `/orders/deposit-returns/${returnId}/complete`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  updateDepositReturnAccount: (
    token: string,
    returnId: string,
    body: UpdateDepositReturnAccountRequest,
  ) =>
    request<{ ok: true }>(BASE, `/orders/deposit-returns/${returnId}/account`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 계약 해지 창 미리보기 */
  terminationPreview: (token: string, id: string) =>
    request<AdminTerminationPreview>(BASE, `/orders/${id}/termination-preview`, { token }),
  /** 계약 해지 — 선불 처리(비환불·부분·전액)를 함께 */
  /** 계약 해지 취소 — 해지와 함께 정한 환불을 송금하기 전에만 */
  cancelTermination: (token: string, id: string, body: CancelTerminationRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/cancel-termination`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  terminateOrder: (token: string, id: string, body: TerminateOrderRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/terminate`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 라이선스 수정 제안 — 구매자·판매자가 모두 동의하면 반영 */
  proposeLicenseAmendment: (token: string, id: string, body: ProposeLicenseAmendmentRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/license-amendments`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 라이선스 수정 제안 철회 — 동의 대기 중인 제안만 */
  cancelLicenseAmendment: (token: string, amendmentId: string) =>
    request<{ ok: true }>(BASE, `/orders/license-amendments/${amendmentId}/cancel`, {
      method: 'POST',
      token,
    }),
  /** 라이선스 사용 중지 — 사유는 구매자·판매자에게 보인다 */
  suspendLicense: (token: string, orderId: string, body: SuspendLicenseRequest) =>
    request<{ ok: true }>(BASE, `/orders/${orderId}/license-suspensions`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 라이선스 사용 중지 해제 */
  resumeLicense: (token: string, suspensionId: string, body: ResumeLicenseRequest) =>
    request<{ ok: true }>(BASE, `/orders/license-suspensions/${suspensionId}/resume`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  /** 분쟁 기각 → 정산 진행 */
  dismissDispute: (token: string, id: string, body: DismissDisputeRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/dispute/dismiss`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  createRefund: (token: string, id: string, body: CreateRefundRequest) =>
    request<AdminRefundResponse>(BASE, `/orders/${id}/refunds`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  listSponsorships: (token: string, query: Partial<AdminListSponsorshipsQuery>) =>
    request<AdminSponsorshipPage>(BASE, '/sponsorships', {
      token,
      query: query as Record<string, string>,
    }),
  listSponsorshipApplications: (
    token: string,
    query: Partial<AdminListSponsorshipApplicationsQuery>,
  ) =>
    request<AdminSponsorshipApplicationPage>(BASE, '/sponsorships/applications', {
      token,
      query: query as Record<string, string>,
    }),
  approveSponsorshipApplication: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/sponsorships/applications/${id}/approve`, {
      method: 'POST',
      token,
    }),
  /** 등록자 대신 거절 — 사유는 지원자에게 그대로 보인다 */
  rejectSponsorshipApplication: (token: string, id: string, reason: string) =>
    request<{ ok: true }>(BASE, `/sponsorships/applications/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    }),
  approveSponsorship: (token: string, id: string, body: SeenVersion) =>
    request<{ ok: true }>(BASE, `/sponsorships/${id}/approve`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  rejectSponsorship: (token: string, id: string, body: RejectSponsorshipRequest) =>
    request<{ ok: true }>(BASE, `/sponsorships/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  takeDownSponsorship: (token: string, id: string, body: TakeDownSponsorshipRequest) =>
    request<{ ok: true }>(BASE, `/sponsorships/${id}/take-down`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  salesSummary: (token: string, query: Partial<AdminSalesQuery>) =>
    request<AdminSalesSummary>(BASE, '/sales/summary', {
      token,
      query: query as Record<string, string>,
    }),
  salesEntries: (token: string, query: Partial<AdminSalesEntriesQuery>) =>
    request<AdminSalesEntriesResponse>(BASE, '/sales/entries', {
      token,
      query: query as Record<string, string>,
    }),
  listProposals: (token: string, query: Partial<AdminListProposalsQuery>) =>
    request<AdminProposalListResponse>(BASE, '/proposals', {
      token,
      query: query as Record<string, string>,
    }),
  closeProposal: (token: string, id: string, body: CloseProposalRequest) =>
    request<{ ok: true }>(BASE, `/proposals/${id}/close`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  listPayments: (token: string, query: Partial<AdminListPaymentsQuery>) =>
    request<AdminPaymentListResponse>(BASE, '/payments', {
      token,
      query: query as Record<string, string>,
    }),
  listDepositReturns: (token: string, query: Partial<AdminListDepositReturnsQuery>) =>
    request<AdminDepositReturnListResponse>(BASE, '/deposit-returns', {
      token,
      query: query as Record<string, string>,
    }),
  listRefunds: (token: string, query: Partial<AdminListRefundsQuery>) =>
    request<AdminRefundListResponse>(BASE, '/refunds', {
      token,
      query: query as Record<string, string>,
    }),
  approveRefund: (token: string, refundId: string) =>
    request<{ ok: true }>(BASE, `/orders/refunds/${refundId}/approve`, { method: 'POST', token }),
  revokeRefundApproval: (token: string, refundId: string, body: RejectRefundRequest) =>
    request<{ ok: true }>(BASE, `/orders/refunds/${refundId}/revoke-approval`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  rejectRefund: (token: string, refundId: string, body: RejectRefundRequest) =>
    request<{ ok: true }>(BASE, `/orders/refunds/${refundId}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  completeRefund: (token: string, refundId: string) =>
    request<{ ok: true }>(BASE, `/orders/refunds/${refundId}/complete`, { method: 'POST', token }),
  confirmPayment: (token: string, id: string, body: ConfirmPaymentRequest) =>
    request<{ ok: true }>(BASE, `/orders/${id}/confirm-payment`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  listSettlements: (token: string, query: Partial<AdminListSettlementsQuery>) =>
    request<AdminSettlementListResponse>(BASE, '/settlements', {
      token,
      query: query as Record<string, string>,
    }),
  holdSettlement: (token: string, id: string, body: HoldSettlementRequest) =>
    request<{ ok: true }>(BASE, `/settlements/${id}/hold`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  releaseSettlement: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/settlements/${id}/release`, {
      method: 'POST',
      token,
    }),
  payOutSettlement: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/settlements/${id}/payout`, {
      method: 'POST',
      token,
      body: JSON.stringify({}),
    }),

  // ── SNS 수동 인증 요청 ──
  // ── 커뮤니티 글 관리 ──
  listCommunityPosts: (token: string, query: Partial<ListAdminCommunityPostsQuery>) =>
    request<AdminCommunityPostPage>(BASE, '/community/posts', {
      token,
      query: query as Record<string, string>,
    }),
  getCommunityPost: (token: string, postId: string) =>
    request<AdminCommunityPostDetail>(BASE, `/community/posts/${postId}`, { token }),
  removeCommunityPost: (token: string, postId: string, body: RemoveCommunityContentRequest) =>
    request<{ ok: true }>(BASE, `/community/posts/${postId}/remove`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  removeCommunityComment: (token: string, commentId: string, body: RemoveCommunityContentRequest) =>
    request<{ ok: true }>(BASE, `/community/comments/${commentId}/remove`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  // ── 고객센터 1:1 문의 ──
  listSupportInquiries: (token: string, query: Partial<ListSupportInquiriesQuery>) =>
    request<AdminSupportInquiryPage>(BASE, '/support/inquiries', {
      token,
      query: query as Record<string, string>,
    }),
  supportWaitingCount: (token: string) =>
    request<{ count: number }>(BASE, '/support/inquiries/waiting-count', { token }),
  getSupportInquiry: (token: string, id: string) =>
    request<AdminSupportInquiryDetail>(BASE, `/support/inquiries/${id}`, { token }),
  replySupportInquiry: (token: string, id: string, body: string) =>
    request<{ ok: true }>(BASE, `/support/inquiries/${id}/messages`, {
      method: 'POST',
      token,
      body: JSON.stringify({ body }),
    }),
  closeSupportInquiry: (token: string, id: string) =>
    request<{ ok: true }>(BASE, `/support/inquiries/${id}/close`, { method: 'POST', token }),

  // ── 신고 검토 ──
  reportPendingCount: (token: string) =>
    request<ReportPendingCount>(BASE, '/reports/pending-count', { token }),
  listCommunityReports: (token: string, query: Partial<ListReportsQuery>) =>
    request<AdminCommunityReportPage>(BASE, '/reports/community', {
      token,
      query: query as Record<string, string>,
    }),
  removeReportedCommunityTarget: (
    token: string,
    targetType: 'POST' | 'COMMENT',
    targetId: string,
    body: ResolveCommunityReportRequest,
  ) =>
    request<{ ok: true }>(BASE, `/reports/community/${targetType}/${targetId}/remove`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  dismissCommunityReports: (
    token: string,
    targetType: 'POST' | 'COMMENT',
    targetId: string,
    body: ResolveCommunityReportRequest,
  ) =>
    request<{ ok: true }>(BASE, `/reports/community/${targetType}/${targetId}/dismiss`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  listChatReports: (token: string, query: Partial<ListReportsQuery>) =>
    request<AdminChatReportPage>(BASE, '/reports/chat', {
      token,
      query: query as Record<string, string>,
    }),
  resolveChatReport: (token: string, reportId: string, body: ResolveChatReportRequest) =>
    request<{ ok: true }>(BASE, `/reports/chat/${reportId}/resolve`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  listSocialChannelRequests: (token: string, query: Partial<ListSocialChannelRequestsQuery>) =>
    request<AdminSocialChannelRequestPage>(BASE, '/social-channel-requests', {
      token,
      query: query as Record<string, string>,
    }),
  approveSocialChannelRequest: (token: string, id: string, body: ApproveSocialChannelRequest) =>
    request<AdminSocialChannelRequest>(BASE, `/social-channel-requests/${id}/approve`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  rejectSocialChannelRequest: (token: string, id: string, body: RejectSocialChannelRequest) =>
    request<AdminSocialChannelRequest>(BASE, `/social-channel-requests/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  // ── 플랫폼 설정 ──
  getPlatformSettings: (token: string) =>
    request<PlatformSettings>(BASE, '/platform-settings', { token }),
  updatePaymentSettings: (token: string, body: UpdatePaymentSettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/payment', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateSiteSettings: (token: string, body: UpdateSiteSettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/site', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateCommunitySettings: (token: string, body: UpdateCommunitySettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/community', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateSponsorshipSettings: (token: string, body: UpdateSponsorshipSettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/sponsorship', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateListingSettings: (token: string, body: UpdateListingSettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/listing', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateCompanySettings: (token: string, body: UpdateCompanySettingsRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/company', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  updateSocialLinks: (token: string, body: UpdatePlatformSocialLinksRequest) =>
    request<PlatformSettings>(BASE, '/platform-settings/social-links', {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  /** 파비콘/OG 이미지 첨부 — multipart. content-type 은 브라우저가 boundary 와 함께 채운다 */
  uploadPlatformAsset: (token: string, key: PlatformAssetKey, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return requestMultipart<PlatformSettings>(`/platform-settings/assets/${key}`, token, body);
  },
  deletePlatformAsset: (token: string, key: PlatformAssetKey) =>
    request<PlatformSettings>(BASE, `/platform-settings/assets/${key}`, {
      method: 'DELETE',
      token,
    }),
  /** 첨부 자산 공개 URL — <img>/<link rel=icon> 에 바로 쓴다. meta 가 있으면 updatedAt 으로 캐시 버스팅 */
  platformAssetUrl: (key: PlatformAssetKey, meta?: PlatformAssetMeta | null) =>
    `${BASE}/api/v1/platform-settings/assets/${key}` +
    (meta ? `?v=${encodeURIComponent(meta.updatedAt)}` : ''),

  // ── 앱 버전 관리 ──
  listAppVersions: (token: string) =>
    request<AppVersionResponse[]>(BASE, '/app-versions', { token }),
  updateAppVersion: (token: string, platform: AppPlatform, body: UpdateAppVersionRequest) =>
    request<AppVersionResponse>(BASE, `/app-versions/${platform}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),

  // ── 스케줄러 관리 ──
  listSchedulerJobs: (token: string) =>
    request<SchedulerJobResponse[]>(BASE, '/scheduler-jobs', { token }),
  updateSchedulerJob: (token: string, key: SchedulerJobKey, body: UpdateSchedulerJobRequest) =>
    request<SchedulerJobResponse>(BASE, `/scheduler-jobs/${key}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(body),
    }),
  /** 지금 실행 요청 — 스케줄러가 15초 안에 집어 실행. 이미 대기 중이면 409 */
  runSchedulerJob: (token: string, key: SchedulerJobKey) =>
    request<SchedulerJobResponse>(BASE, `/scheduler-jobs/${key}/run`, { method: 'POST', token }),

  // ── 채팅 관리 (api-admin) ──
  listChatRooms: (token: string, query: Partial<AdminListChatRoomsQuery>) =>
    request<AdminChatRoomResponse[]>(BASE, '/chat/rooms', {
      token,
      query: query as Record<string, string>,
    }),
  getChatRoom: (token: string, roomId: string) =>
    request<AdminChatRoomDetailResponse>(BASE, `/chat/rooms/${roomId}`, { token }),
  getChatRoomMessages: (
    token: string,
    roomId: string,
    query: Partial<AdminGetChatMessagesQuery> = {},
  ) =>
    request<AdminChatMessageResponse[]>(BASE, `/chat/rooms/${roomId}/messages`, {
      token,
      query: query as Record<string, string>,
    }),
  joinChatRoom: (token: string, roomId: string) =>
    request<AdminJoinChatRoomResponse>(BASE, `/chat/rooms/${roomId}/join`, {
      method: 'POST',
      token,
    }),
  leaveChatRoom: (token: string, roomId: string) =>
    request<AdminLeaveChatRoomResponse>(BASE, `/chat/rooms/${roomId}/leave`, {
      method: 'POST',
      token,
    }),
};
